/*
 * SrRegion — геометрия «Области экрана» записи экрана (24.10.2026, чат 713).
 *
 * Человек выбирает «Весь экран» и обводит мышью часть — например, окно игры.
 * Область хранится ДОЛЯМИ кадра {x, y, w, h} (0…1): один и тот же прямоугольник
 * понимают выбор на превью (пиксели превью), движок записи (пиксели кадра
 * захвата, visibleRect) и композитор (холст), и он переживает смену
 * разрешения экрана посреди дубля.
 *
 * Здесь только чистая математика — её гоняет tests/js/screen-recorder-region.test.mjs.
 * Потребители: recorder.js (обрезка кадра), presenter-overlay.js и air-draw.js
 * (обрезка источника в композите), area-picker.js (выбор на превью).
 */
(function (root) {
    'use strict';

    // Меньше — уже не «область», а промах мышью; и кодировщики H.264 на
    // крошечных кадрах ведут себя непредсказуемо.
    var MIN_PX = 64;

    function num(v) { return typeof v === 'number' && isFinite(v); }
    function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
    function even(v) { return Math.floor(v / 2) * 2; }

    // Доли → проверенные доли или null. Весь кадр — не область (нечего резать).
    function normalize(r) {
        if (!r || !num(r.x) || !num(r.y) || !num(r.w) || !num(r.h)) return null;
        var x = clamp(r.x, 0, 1), y = clamp(r.y, 0, 1);
        var w = clamp(r.w, 0, 1 - x), h = clamp(r.h, 0, 1 - y);
        if (!(w > 0) || !(h > 0)) return null;
        if (x < 0.002 && y < 0.002 && w > 0.996 && h > 0.996) return null;
        return { x: x, y: y, w: w, h: h };
    }

    // Доли → пиксели кадра. `vr` — visibleRect кадра (у захвата бывает меньше
    // coded-размера: 1920×1088 → 1920×1080), иначе весь coded-кадр. Начало и
    // стороны чётные: 4:2:0 хочет чётных. null — область меньше MIN_PX.
    function toPixels(R, vr, codedW, codedH) {
        R = normalize(R);
        if (!R) return null;
        var bx = vr ? (vr.x | 0) : 0, by = vr ? (vr.y | 0) : 0;
        var bw = vr ? vr.width : codedW, bh = vr ? vr.height : codedH;
        if (!(bw > 0) || !(bh > 0)) return null;
        var x = even(bx + Math.round(R.x * bw)), y = even(by + Math.round(R.y * bh));
        if (x < bx) x += 2;
        if (y < by) y += 2;
        var w = even(Math.round(R.w * bw)), h = even(Math.round(R.h * bh));
        if (x + w > bx + bw) w = even(bx + bw - x);
        if (y + h > by + bh) h = even(by + bh - y);
        if (w < MIN_PX || h < MIN_PX) return null;
        return { x: x, y: y, w: w, h: h };
    }

    // ── выбор на превью: всё в пикселях ИСТОЧНИКА (srcW × srcH) ────────────

    function toPx(R, W, H) { return { x: R.x * W, y: R.y * H, w: R.w * W, h: R.h * H }; }
    function toFrac(p, W, H) { return { x: p.x / W, y: p.y / H, w: p.w / W, h: p.h / H }; }

    // Прямоугольник целиком внутри кадра, не меньше MIN_PX (или кадра, если он меньше).
    function keepInside(p, W, H) {
        var mw = Math.min(MIN_PX, W), mh = Math.min(MIN_PX, H);
        var w = clamp(p.w, mw, W), h = clamp(p.h, mh, H);
        var x = clamp(p.x, 0, W - w), y = clamp(p.y, 0, H - h);
        return { x: x, y: y, w: w, h: h };
    }

    // Пропорция ar = ширина/высота в пикселях. Центр остаётся на месте, площадь
    // по возможности та же; упирается в край — уменьшается, а не вылезает.
    function withAspect(R, ar, W, H) {
        if (!(ar > 0)) return R;
        var p = toPx(R, W, H);
        var cx = p.x + p.w / 2, cy = p.y + p.h / 2;
        var area = Math.max(p.w * p.h, MIN_PX * MIN_PX);
        var w = Math.sqrt(area * ar), h = w / ar;
        var k = Math.min(1, W / w, H / h);
        w *= k; h *= k;
        // по краям кадра: центр сдвигается внутрь
        var x = clamp(cx - w / 2, 0, W - w), y = clamp(cy - h / 2, 0, H - h);
        return toFrac({ x: x, y: y, w: w, h: h }, W, H);
    }

    function moveBy(R, dx, dy, W, H) {
        var p = toPx(R, W, H);
        p.x = clamp(p.x + dx, 0, W - p.w);
        p.y = clamp(p.y + dy, 0, H - p.h);
        return toFrac(p, W, H);
    }

    // Тянем ручку `h` ('n','s','e','w','ne','nw','se','sw') на (dx, dy) пикселей
    // источника. Противоположный край стоит. С пропорцией главная ось — та, по
    // которой тянут больше; угол держит противоположный угол.
    function resizeBy(R, h, dx, dy, W, H, ar) {
        var p = toPx(R, W, H);
        var l = p.x, t = p.y, r = p.x + p.w, b = p.y + p.h;
        var mw = Math.min(MIN_PX, W), mh = Math.min(MIN_PX, H);
        if (h.indexOf('w') !== -1) l = clamp(l + dx, 0, r - mw);
        if (h.indexOf('e') !== -1) r = clamp(r + dx, l + mw, W);
        if (h.indexOf('n') !== -1) t = clamp(t + dy, 0, b - mh);
        if (h.indexOf('s') !== -1) b = clamp(b + dy, t + mh, H);
        if (ar > 0) {
            var w = r - l, hh = b - t;
            var horiz = (h === 'e' || h === 'w'), vert = (h === 'n' || h === 's');
            var byW = horiz || (!vert && Math.abs(dx) >= Math.abs(dy));
            if (byW) hh = w / ar; else w = hh * ar;
            // не за кадр: ужимаем обе стороны пропорционально
            var ax = h.indexOf('w') !== -1 ? r : l;            // неподвижный край по x
            var ay = h.indexOf('n') !== -1 ? b : t;            // неподвижный край по y
            var maxW = h.indexOf('w') !== -1 ? ax : W - ax;
            var maxH = h.indexOf('n') !== -1 ? ay : H - ay;
            if (horiz) maxH = H;                               // по вертикали — от центра
            if (vert) maxW = W;
            var k = Math.min(1, maxW / w, maxH / hh);
            w *= k; hh *= k;
            if (horiz || vert) {
                // боковая ручка: вторая ось растёт от середины
                if (horiz) { var cy = (t + b) / 2; t = clamp(cy - hh / 2, 0, H - hh); b = t + hh; l = h === 'w' ? r - w : l; r = l + w; }
                else { var cx = (l + r) / 2; l = clamp(cx - w / 2, 0, W - w); r = l + w; t = h === 'n' ? b - hh : t; b = t + hh; }
            } else {
                if (h.indexOf('w') !== -1) l = ax - w; else r = ax + w;
                if (h.indexOf('n') !== -1) t = ay - hh; else b = ay + hh;
            }
        }
        return toFrac(keepInside({ x: l, y: t, w: r - l, h: b - t }, W, H), W, H);
    }

    // Новый прямоугольник протяжкой от точки a к точке b (пиксели источника).
    function fromDrag(a, b, W, H, ar) {
        var x0 = clamp(a.x, 0, W), y0 = clamp(a.y, 0, H);
        var x1 = clamp(b.x, 0, W), y1 = clamp(b.y, 0, H);
        var w = Math.abs(x1 - x0), h = Math.abs(y1 - y0);
        if (ar > 0) {
            if (w / ar >= h) h = w / ar; else w = h * ar;
            var maxW = x1 >= x0 ? W - x0 : x0, maxH = y1 >= y0 ? H - y0 : y0;
            var k = Math.min(1, maxW / Math.max(w, 1), maxH / Math.max(h, 1));
            w *= k; h *= k;
        }
        var x = x1 >= x0 ? x0 : x0 - w, y = y1 >= y0 ? y0 : y0 - h;
        return toFrac(keepInside({ x: x, y: y, w: w, h: h }, W, H), W, H);
    }

    // Размер файла, который даст область (то, что пишется на ручке): пиксели
    // кадра, чётные — как в toPixels.
    function sizeOf(R, W, H) {
        var p = toPixels(R, null, W, H);
        return p ? { w: p.w, h: p.h } : { w: even(W), h: even(H) };
    }

    // Запомненная область годится, если источник того же размера: доли с
    // экрана 2560×1440 на экране 1920×1080 указали бы не туда.
    function key(W, H) { return Math.round(W) + 'x' + Math.round(H); }

    var api = {
        MIN_PX: MIN_PX,
        normalize: normalize, toPixels: toPixels,
        withAspect: withAspect, moveBy: moveBy, resizeBy: resizeBy, fromDrag: fromDrag,
        sizeOf: sizeOf, key: key
    };
    root.SrRegion = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
